import type {
    DataSourceAbstract,
    DateVariableValue,
    FieldADTValue,
    FilterBlockItemConfig,
    FilterOption,
    VariableADTvalue
} from '@lighthouse/core'
import { FilterWay, getFilterBlockItemDefaultValue, NumberRangeMode } from '@lighthouse/core'
import type { ComplexDataOfVariableAndJSONContentData, I18TranslationKey, ParseVariableOptions } from '@lighthouse/shared'
import {
    dateVariableValueToI18nKeyMap,
    recTakeFilterVariable,
    resolveFilter,
    useLanguageContext,
    USER_DATASOURCE
} from '@lighthouse/shared'
import { useDebounce } from '@lighthouse/tools'
import equal from 'fast-deep-equal'
import { find } from 'rambda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLatest } from 'react-use'

import { CUSTOM_VALUE } from './constants'
import type { GetCascadeOptionPayload, GetTextOptionPayload } from './types'

type GetOptionPayload = {
    value?: string[]
    config: FilterBlockItemConfig
    dataSourceList: DataSourceAbstract[]
    onChange?: (val: string[]) => void
    onFetchTextOptions?: (params: GetTextOptionPayload) => Promise<FilterOption[]>
    onFetchCascadeOptions?: (params: GetCascadeOptionPayload) => Promise<FilterOption[]>
    useParseVariableWithSubscription: (
        data?: ComplexDataOfVariableAndJSONContentData
    ) => (value: VariableADTvalue, option?: ParseVariableOptions) => FieldADTValue | undefined
}

const boolOptions: FilterOption[] = [
    {
        label: 'yes',
        value: 'true'
    },
    {
        label: 'no',
        value: 'false'
    }
]

/**
 * 过滤掉不在 options 中的 value
 */
const filterValidValues = (currentValue: string[] | undefined, options: FilterOption[], onChange?: (val: string[]) => void) => {
    if (!currentValue?.length) {
        return
    }

    const optionValues = new Set(options.map(item => item.value))
    const existValues = currentValue.filter(v => optionValues.has(v))

    if (existValues.length !== currentValue.length) {
        onChange?.(existValues)
    }
}

export const useGetOption = (params: GetOptionPayload) => {
    const {
        value,
        config: paramsConfig,
        dataSourceList,
        onChange,
        onFetchTextOptions,
        onFetchCascadeOptions,
        useParseVariableWithSubscription
    } = params
    const firstRef = useRef(true)
    const initValue = useLatest(value)
    const config = useDebounce(paramsConfig, 500)

    const configRef = useRef<FilterBlockItemConfig | undefined>()
    const [loading, setLoading] = useState(false)
    const [RecordOptions, setRecordOptions] = useState<FilterOption[]>([])
    // [FilterWay.textFilter, FilterWay.personFilter, FilterWay.cascadeFilter]
    const parseDependencies = useMemo(() => {
        const dependencyList: VariableADTvalue[] = []

        if (
            (config.filterWay === FilterWay.textFilter ||
                config.filterWay === FilterWay.personFilter ||
                config.filterWay === FilterWay.cascadeFilter) &&
            config.filter
        ) {
            dependencyList.push(...(recTakeFilterVariable(config.filter.expression) || []))
        }

        if (config.filterWay === FilterWay.textFilter && config.textDefaultVariables) {
            dependencyList.push(config.textDefaultVariables)
        }

        return dependencyList.length > 0 ? dependencyList : undefined
    }, [config])
    const parseVariable2Field = useParseVariableWithSubscription(parseDependencies)
    const parseConfig = useMemo(() => {
        if (
            (config.filterWay === FilterWay.textFilter ||
                config.filterWay === FilterWay.personFilter ||
                config.filterWay === FilterWay.cascadeFilter) &&
            config.filter
        ) {
            return {
                ...config,
                filter: resolveFilter({
                    filter: config.filter,
                    extraParams: {
                        parseVariable2Field
                    }
                })
            }
        }
        return config
    }, [config, parseVariable2Field])
    const resolvedTextDefaultValue = useMemo(() => {
        if (config.filterWay === FilterWay.textFilter && config.textDefaultVariables) {
            return parseVariable2Field(config.textDefaultVariables)
        }

        return
    }, [config, parseVariable2Field])
    const { convertTextByLanguage } = useLanguageContext()

    const getPersonRecordOptions = useCallback(
        async (params: GetTextOptionPayload) => {
            setLoading(true)

            const recordOptions = await onFetchTextOptions?.(params)
            setLoading(false)
            if (recordOptions) {
                filterValidValues(initValue.current, recordOptions, onChange)
                setRecordOptions(recordOptions)
            }
        },
        [onChange, onFetchTextOptions, initValue]
    )

    const getTextRecordOptions = useCallback(
        async (params: GetTextOptionPayload) => {
            setLoading(true)
            const recordOptions = await onFetchTextOptions?.(params)
            setLoading(false)
            if (recordOptions) {
                filterValidValues(initValue.current, recordOptions, onChange)
                setRecordOptions(recordOptions)
            }
        },
        [onChange, onFetchTextOptions, initValue]
    )

    const getRecordCascadeOptions = useCallback(
        async (params: GetCascadeOptionPayload) => {
            const { fieldPointer, parentFieldPointer, dsId, showFieldPointer } = params
            const isEmptyConfig = !fieldPointer || !parentFieldPointer || !dsId || !showFieldPointer
            if (isEmptyConfig) {
                return
            }
            setLoading(true)
            const options = await onFetchCascadeOptions?.(params)
            setLoading(false)
            if (options) {
                // 过滤无效值
                filterValidValues(initValue.current, options, onChange)
                setRecordOptions(options)
            }
        },
        [onFetchCascadeOptions, initValue, onChange]
    )

    const getNumberOptions = useCallback((): FilterOption[] => {
        if (config.filterWay !== FilterWay.numberFilter) {
            return []
        }

        const validateNumberOptions = config.numberOptions?.filter(item => {
            return item.label && item.value && (item.value[0] !== undefined || item.value[1] !== undefined)
        })
        const options =
            validateNumberOptions?.map(v => ({
                label: v.label,
                value: v.id
            })) || []
        if (config.numberRangeMode && config.numberRangeMode !== NumberRangeMode.disable) {
            options.push({
                label: convertTextByLanguage('custom'),
                value: CUSTOM_VALUE
            })
        }

        // 过滤无效值
        filterValidValues(initValue.current, options, onChange)

        return options
    }, [config, convertTextByLanguage, initValue, onChange])

    const getDateOptions = useCallback((): FilterOption[] => {
        if (config.filterWay !== FilterWay.dateFilter) {
            return []
        }

        const options =
            config.dateVariables?.map(v => {
                const i18nKey = dateVariableValueToI18nKeyMap[v as DateVariableValue]
                return {
                    label: i18nKey ? convertTextByLanguage(i18nKey) : '',
                    value: v
                }
            }) || []
        if (config.isDateRange) {
            options.push({
                label: convertTextByLanguage('custom'),
                value: CUSTOM_VALUE
            })
        }

        // 过滤无效值
        filterValidValues(initValue.current, options, onChange)

        return options
    }, [config, convertTextByLanguage, initValue, onChange])

    const getSelectOptions = useCallback((): FilterOption[] => {
        if (config.filterWay !== FilterWay.selectFilter) {
            return []
        }

        const ds = find(item => item.id === config.pointer, dataSourceList)
        if (!ds) {
            return []
        }
        const { schema } = ds
        const field = schema[config.fieldPointer || '']
        if (!field || field.type !== 'select') {
            return []
        }
        const customOpts = config.customOptions
        const options = field.select.options.reduce<FilterOption[]>((prev, cur) => {
            if (customOpts && customOpts.includes(cur.label)) {
                prev.push({
                    label: cur.label,
                    value: cur.label
                })
                return prev
            }
            return prev
        }, [])

        // 过滤无效值
        filterValidValues(initValue.current, options, onChange)

        return options
    }, [config, dataSourceList, initValue, onChange])

    const getBoolOptions = useCallback((): FilterOption[] => {
        const options = boolOptions.map(v => ({
            ...v,
            label: convertTextByLanguage(v.label as I18TranslationKey)
        }))

        // 过滤无效值
        filterValidValues(initValue.current, options, onChange)

        return options
    }, [convertTextByLanguage, initValue, onChange])

    const handleFetchOption = useCallback(
        (config: FilterBlockItemConfig) => {
            if (config.filterWay === FilterWay.textFilter && config.pointer && config.fieldPointer && config.showFieldPointer) {
                getTextRecordOptions({
                    dsId: config.pointer,
                    fieldId: config.fieldPointer,
                    showFieldId: config.showFieldPointer,
                    filterItemId: config.id,
                    filter: config.filter,
                    sorts: config.sorts
                })
            }
            if (config.filterWay === FilterWay.personFilter) {
                getPersonRecordOptions({
                    dsId: USER_DATASOURCE,
                    fieldId: 'ID',
                    showFieldId: 'USERNAME',
                    filterItemId: config.id,
                    filter: config.filter,
                    sorts: config.sorts
                })
            }
            if (config.filterWay === FilterWay.cascadeFilter) {
                const {
                    id,
                    parentFieldPointer = '',
                    fieldPointer = '',
                    sortFieldPointer,
                    pointer = '',
                    showFieldPointer = '',
                    filter
                } = config
                getRecordCascadeOptions({
                    dsId: pointer,
                    fieldPointer,
                    showFieldPointer,
                    parentFieldPointer,
                    sortFieldPointer,
                    filter,
                    filterItemId: id
                })
            }
        },
        [getTextRecordOptions, getPersonRecordOptions, getRecordCascadeOptions]
    )

    const onRetry = useCallback(() => {
        return handleFetchOption(config)
    }, [config, handleFetchOption])

    useEffect(() => {
        if (!parseConfig || equal(configRef.current, parseConfig)) {
            return
        }
        configRef.current = parseConfig
        handleFetchOption(parseConfig)
    }, [handleFetchOption, parseConfig])

    // TODO: 刷新数据时的依赖暂不确定，例如： 翻页、url路径参数变化
    // useUpdateEffect(() => {
    //     handleFetchOption(config)
    // }, [recordId])

    const options: FilterOption[] = useMemo(() => {
        switch (config.filterWay) {
            case FilterWay.textFilter:
            case FilterWay.personFilter:
            case FilterWay.cascadeFilter: {
                return RecordOptions
            }
            case FilterWay.numberFilter: {
                return getNumberOptions()
            }
            case FilterWay.dateFilter: {
                return getDateOptions()
            }
            case FilterWay.selectFilter: {
                return getSelectOptions()
            }
            case FilterWay.boolFilter: {
                return getBoolOptions()
            }
            default: {
                return []
            }
        }
    }, [RecordOptions, config.filterWay, getNumberOptions, getDateOptions, getSelectOptions, getBoolOptions])

    useEffect(() => {
        if (options.length === 0) {
            return
        }

        const isFirstEmptyValue = firstRef.current && Array.isArray(value) && value.length === 0

        if (!isFirstEmptyValue && value !== undefined) {
            firstRef.current = false
            return
        }

        const defaultValue = getFilterBlockItemDefaultValue(
            config,
            options.map(item => item.value),
            resolvedTextDefaultValue
        )
        if (!defaultValue?.value.length) {
            return
        }
        firstRef.current = false

        onChange?.(defaultValue.value)
    }, [config, onChange, options, resolvedTextDefaultValue, value])

    return { options, loading, retry: onRetry }
}
